import { useState } from 'react';
import { CalendarDays, Clock, FileImage, MapPin, Phone, Trash2, Truck, UserRound } from 'lucide-react';
import { contactOf, formatTaskDate, formatTaskWindow, taskTypeName } from '../../core/task-filters';
import { AuditStamp } from '../../components/ui/AuditStamp';
import type { DispatchCard } from '../../core/dispatch-card';
import { DispatchOrderCard } from './DispatchOrderCard';
import { ShareDispatchButton } from './ShareDispatchButton';
import {
  TASK_PRIORITY_LABELS,
  TASK_PRIORITY_TONES,
  taskStatusLabel,
  taskStatusTone,
} from '../../types/task';
import type { Task, TaskType } from '../../types/task';
import type { Contact } from '../../types/contact';

interface TaskCardProps {
  task: Task;
  taskTypes: readonly TaskType[];
  contacts: readonly Contact[];
  /** Evaluation moment, so "היום" never depends on when the card re-rendered. */
  now: number;
  /** Omitted for a viewer who may not edit — the card is then not clickable. */
  onOpen?: (task: Task) => void;
  /** Opens the vehicle-team dialog. Turns the card into an action row. */
  onManageTeams?: (task: Task) => void;
  /** Opens the role-based detail view — the manager's screen or the field one. */
  onOpenDetail?: (task: Task) => void;
  /**
   * Quick delete. Omitted for a viewer without `DELETE_TASK`; the board asks
   * for confirmation before the task is moved to the recycle bin.
   */
  onDelete?: (task: Task) => void;
  /**
   * The portrait dispatch order of this task (פקודת משימה — תצוגה לאורך), built
   * by the board through `buildTaskDispatchCard()` and already scoped to what
   * this viewer may see. When it is handed over the card can open the order and
   * share it as an image; without it the card is the summary it always was.
   */
  dispatchCard?: DispatchCard | null;
}

/** One task on the board: what, when, with whom and where it stands. */
export function TaskCard({
  task,
  taskTypes,
  contacts,
  now,
  onOpen,
  onManageTeams,
  onOpenDetail,
  onDelete,
  dispatchCard,
}: TaskCardProps) {
  const [orderOpen, setOrderOpen] = useState(false);
  const typeName = taskTypeName(task.taskTypeId, taskTypes);
  const contact = contactOf(task, contacts);
  const window = formatTaskWindow(task);

  const body = (
    <>
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-bold truncate">{task.title}</h3>
          {typeName ? (
            <span className="mt-1 inline-block text-xs px-2 py-0.5 rounded-lg border border-line bg-canvas text-ink/60">
              {typeName}
            </span>
          ) : null}
        </div>
        <span
          className={[
            'shrink-0 text-xs px-2 py-1 rounded-lg border font-medium',
            TASK_PRIORITY_TONES[task.priority],
          ].join(' ')}
        >
          {TASK_PRIORITY_LABELS[task.priority]}
        </span>
      </header>

      <div className="mt-3 space-y-1.5 text-sm text-ink/70">
        <p className="flex items-center gap-2">
          <CalendarDays size={15} className="text-ink/40 shrink-0" aria-hidden />
          <span>{formatTaskDate(task.date, now)}</span>
          {window ? (
            <>
              <Clock size={15} className="text-ink/40 shrink-0" aria-hidden />
              <span data-dir="ltr" className="tabular-nums">
                {window}
              </span>
            </>
          ) : null}
        </p>

        {contact ? (
          <p className="flex items-center gap-2 min-w-0">
            <UserRound size={15} className="text-ink/40 shrink-0" aria-hidden />
            <span className="truncate">{contact.name}</span>
            {contact.phone ? (
              <>
                <Phone size={15} className="text-ink/40 shrink-0" aria-hidden />
                <span data-dir="ltr" className="tabular-nums">
                  {contact.phone}
                </span>
              </>
            ) : null}
          </p>
        ) : null}

        {task.destinationAddress ? (
          <p className="flex items-center gap-2 min-w-0">
            <MapPin size={15} className="text-ink/40 shrink-0" aria-hidden />
            <span className="truncate">{task.destinationAddress}</span>
          </p>
        ) : null}
      </div>

      <footer className="mt-3 flex items-center justify-between gap-2">
        <span
          className={[
            'text-xs px-2 py-1 rounded-lg border',
            taskStatusTone(task.statusId),
          ].join(' ')}
        >
          {taskStatusLabel(task.statusId)}
        </span>
        {task.operationalNotes ? (
          <span className="text-xs text-ink/40 truncate">{task.operationalNotes}</span>
        ) : null}
      </footer>

      {/* "מי עידכן מה" — who last touched this task, read straight off the row.
          Nothing is rendered for a task that does not say. */}
      <AuditStamp record={task} now={now} className="mt-2" />
    </>
  );

  // `w-full min-w-0 box-border`: the card fills its column exactly and never
  // measures itself by its widest line. Padding is inside that width, so a
  // phone-sized track holds the card rather than the card widening the page.
  const shell = 'bg-panel border border-line rounded-2xl p-4 text-right w-full min-w-0 box-border';

  // With an assignment action the whole card can no longer be one button, so
  // the ways into the task move to an explicit row underneath it.
  if (onManageTeams || onOpenDetail || onDelete || dispatchCard) {
    return (
      <article className={shell}>
        {body}

        {dispatchCard ? (
          <div className="mt-3 space-y-2">
            <ShareDispatchButton card={dispatchCard} />
            <button
              type="button"
              onClick={() => setOrderOpen((open) => !open)}
              aria-expanded={orderOpen}
              className="h-9 px-3 rounded-xl border border-line text-sm text-ink/70 hover:text-ink flex items-center gap-1.5"
            >
              <FileImage size={15} aria-hidden />
              {orderOpen ? 'סגירת פקודת המשימה' : 'פקודת משימה לאורך'}
            </button>
            {orderOpen ? <DispatchOrderCard card={dispatchCard} /> : null}
          </div>
        ) : null}

        <div className="mt-3 pt-3 border-t border-line flex flex-wrap gap-2">
          {onOpenDetail ? (
            <button
              type="button"
              onClick={() => onOpenDetail(task)}
              className="h-9 px-3 rounded-xl border border-brand-500 bg-brand-500 text-white text-sm font-medium hover:bg-brand-700"
            >
              פתיחת המשימה
            </button>
          ) : null}
          {onOpen ? (
            <button
              type="button"
              onClick={() => onOpen(task)}
              className="h-9 px-3 rounded-xl border border-line text-sm text-ink/70 hover:text-ink"
            >
              פרטים ועריכה
            </button>
          ) : null}
          {onManageTeams ? (
            <button
              type="button"
              onClick={() => onManageTeams(task)}
              className="h-9 px-3 rounded-xl border border-brand-300 bg-brand-50 text-brand-700 text-sm font-medium hover:bg-brand-100 flex items-center gap-1.5"
            >
              <Truck size={15} aria-hidden />
              צוותים ורכבים
            </button>
          ) : null}
          {onDelete ? (
            <button
              type="button"
              onClick={() => onDelete(task)}
              title="מחיקת משימה"
              className="h-9 px-3 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-sm font-medium hover:bg-rose-100 flex items-center gap-1.5"
            >
              <Trash2 size={15} aria-hidden />
              מחיקה
            </button>
          ) : null}
        </div>
      </article>
    );
  }

  if (!onOpen) return <article className={shell}>{body}</article>;

  return (
    <button
      type="button"
      onClick={() => onOpen(task)}
      className={`${shell} hover:border-brand-300 transition-colors`}
    >
      {body}
    </button>
  );
}
